// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the corner's "I'll remember that" card (design/assistant-memory c-saved). When the
// assistant saves a note about me (its `remember` tool) the server sends KONVO_ASSISTANT_ACTIVITY
// `{memory: {id, text, topic}}`; the note waits here until the card can be drawn (CornerMemoryQueue
// .canDrawMemory) and then shows with Change, Undo and See what I know. Nothing goes into memory
// silently. It is local to this app: not a server suggestion, so no outcomes are posted.
//
// Notes saved while the card is up join it (up to three rows, then "+N more"); a suggestion card
// that is up goes first, and no new suggestion goes up over this card. It closes after 12 s unless
// the pointer or focus is on it. While Settings › What Konvo knows is open the page shows the note
// itself (attachKnowsPage) and no card goes up.

import MobileRailPanelState from '@app/features/app/components/layout/mobile_rail/MobileRailPanelState';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import Corner from '@app/features/konvo_corner/state/Corner';
import {
	addNote,
	canDrawMemory,
	MEMORY_AUTO_CLOSE_MS,
	mergeNotes,
	type RememberedNote,
	removeNote,
} from '@app/features/konvo_corner/state/CornerMemoryQueue';
import {resumeAutoCloseMs} from '@app/features/konvo_corner/state/CornerQueue';
import KonvoKnows from '@app/features/konvo_knows/state/KonvoKnows';
import KonvoPanel from '@app/features/konvo_panel/state/KonvoPanel';
import HomePeek from '@app/features/konvo_today/state/HomePeek';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import {Logger} from '@app/features/platform/utils/AppLogger';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import Modal from '@app/features/ui/state/Modal';
import {makeAutoObservable, reaction} from 'mobx';

const logger = new Logger('CornerMemory');

/** What the What Konvo knows page registers while it is open (see attachKnowsPage). */
export interface KnowsPageHook {
	/** Load the notes again: one was just saved or forgotten (KonvoKnows.refresh() runs anyway once loaded). */
	refresh?: () => void;
}

let knowsPageIds = 0;

export class CornerMemoryStore {
	/** Saved notes waiting for the card, newest first. */
	pending: Array<RememberedNote> = [];
	/** The card: its notes, newest first (null while none is up). */
	visible: {notes: Array<RememberedNote>; shownAt: number} | null = null;
	/**
	 * The note "Change" asked Settings › What Konvo knows to open in edit mode. The page reads it
	 * with takeFocusNote() when it mounts (and reacts to it while open), then it is cleared.
	 */
	focusNoteId: string | null = null;
	/** Undo requests on their way, by note id (the row's Undo shows busy). */
	forgetting = new Set<string>();
	/** What Konvo knows pages open right now (attachKnowsPage). */
	knowsPages = new Map<number, KnowsPageHook>();

	private seen = new Set<string>();
	private started = false;
	private stops: Array<() => void> = [];
	private autoClose: ReturnType<typeof setTimeout> | null = null;
	private autoCloseDeadline: number | null = null;
	private autoCloseRemaining: number | null = null;
	private hiddenSince: number | null = null;
	private pointerOver = false;
	private focusInside = false;
	/** Bumped on reset: Undo replies for the previous person are dropped. */
	private generation = 0;

	constructor() {
		makeAutoObservable<
			this,
			| 'seen'
			| 'started'
			| 'stops'
			| 'autoClose'
			| 'autoCloseDeadline'
			| 'autoCloseRemaining'
			| 'hiddenSince'
			| 'pointerOver'
			| 'focusInside'
			| 'generation'
		>(
			this,
			{
				seen: false,
				started: false,
				stops: false,
				autoClose: false,
				autoCloseDeadline: false,
				autoCloseRemaining: false,
				hiddenSince: false,
				pointerOver: false,
				focusInside: false,
				generation: false,
			},
			{autoBind: true},
		);
	}

	get knowsPageOpen(): boolean {
		return this.knowsPages.size > 0;
	}

	/** The card can be seen right now (see canDrawMemory). */
	get canDraw(): boolean {
		return canDrawMemory({
			phone: MobileLayout.enabled,
			panelOpen: KonvoPanel.isOpen,
			modalOpen: Modal.hasModalOpen(),
			homePeekOpen: HomePeek.isOpen,
			suggestionShown: Corner.card != null,
			knowsPageOpen: this.knowsPageOpen,
			phoneBar: Corner.phoneBars > 0,
			railPanelOpen: MobileRailPanelState.open != null,
			keyboardOpen: Corner.keyboardOpen,
		});
	}

	/** The notes to draw now (null while the card cannot be seen). */
	get card(): Array<RememberedNote> | null {
		if (!this.visible || !this.canDraw) return null;
		return this.visible.notes;
	}

	/** Start once signed in (CornerAssistant); the returned function stops and forgets. */
	start(): () => void {
		if (this.started) return () => {};
		this.started = true;
		this.stops = [
			reaction(
				() => this.canDraw,
				(can) => {
					this.syncAutoClose();
					if (can) this.evaluate();
				},
			),
			// No new suggestion goes up over this card; once it is down the corner looks again.
			reaction(
				() => this.card != null,
				(up) => Corner.setLocalCard(up),
				{fireImmediately: true},
			),
		];
		this.evaluate();
		return () => this.stop();
	}

	stop(): void {
		for (const stop of this.stops) stop();
		this.stops = [];
		this.started = false;
		this.reset();
	}

	reset(): void {
		this.generation += 1;
		this.clearAutoClose();
		this.autoCloseRemaining = null;
		this.hiddenSince = null;
		this.pointerOver = false;
		this.focusInside = false;
		this.pending = [];
		this.visible = null;
		this.focusNoteId = null;
		this.forgetting = new Set();
		this.seen.clear();
		Corner.setLocalCard(false);
	}

	/** Gateway: the assistant saved a note. */
	receive(note: RememberedNote): void {
		if (this.seen.has(note.id)) return;
		// The page is open: it shows the note itself.
		if (this.knowsPageOpen) {
			this.seen.add(note.id);
			this.refreshKnowsPages();
			return;
		}
		this.pending = addNote(this.pending, note, this.seen);
		this.seen.add(note.id);
		this.refreshKnowsPages();
		if (this.visible) {
			// Joins the card that is up, with a fresh clock.
			this.visible = {notes: mergeNotes(this.pending, this.visible.notes), shownAt: this.visible.shownAt};
			this.pending = [];
			this.autoCloseRemaining = null;
			this.clearAutoClose();
			this.syncAutoClose();
			return;
		}
		this.evaluate();
	}

	/** Put the waiting notes up as one card when it can be seen. */
	evaluate(): void {
		if (!this.started || this.visible || this.pending.length === 0) return;
		if (this.knowsPageOpen) {
			this.pending = [];
			return;
		}
		if (!this.canDraw) return;
		this.clearAutoClose();
		this.autoCloseRemaining = null;
		this.hiddenSince = null;
		this.pointerOver = false;
		this.focusInside = false;
		this.visible = {notes: this.pending, shownAt: Date.now()};
		this.pending = [];
		this.syncAutoClose();
	}

	private clearAutoClose(): void {
		if (this.autoClose) clearTimeout(this.autoClose);
		this.autoClose = null;
		this.autoCloseDeadline = null;
	}

	/**
	 * Run or pause the 12 s auto-close: paused while the pointer or focus is on the card and while it
	 * cannot be seen. After hover or focus it starts over; after a hidden stretch it resumes.
	 */
	private syncAutoClose(): void {
		const now = Date.now();
		const drawable = this.canDraw;
		if (this.visible && !drawable) {
			if (this.hiddenSince == null) this.hiddenSince = now;
		} else {
			this.hiddenSince = null;
		}
		const paused = !this.visible || this.pointerOver || this.focusInside || !drawable;
		if (paused) {
			if (this.autoClose && this.autoCloseDeadline != null) {
				this.autoCloseRemaining = Math.max(0, this.autoCloseDeadline - now);
			}
			this.clearAutoClose();
			return;
		}
		if (this.autoClose) return;
		const wait = resumeAutoCloseMs(this.autoCloseRemaining, MEMORY_AUTO_CLOSE_MS);
		this.autoCloseRemaining = null;
		this.autoCloseDeadline = now + wait;
		this.autoClose = setTimeout(() => this.dismiss(), wait);
	}

	hover(entering: boolean): void {
		if (!this.visible) return;
		this.pointerOver = entering;
		if (!entering) this.autoCloseRemaining = null;
		this.syncAutoClose();
	}

	focusWithin(inside: boolean): void {
		if (!this.visible || this.focusInside === inside) return;
		this.focusInside = inside;
		if (!inside) this.autoCloseRemaining = null;
		this.syncAutoClose();
	}

	/** ✕, Escape or the 12 s: take the card down (the notes stay saved) and show what waits next. */
	dismiss(): void {
		this.takeDown();
		this.evaluate();
	}

	private takeDown(): void {
		this.clearAutoClose();
		this.autoCloseRemaining = null;
		this.hiddenSince = null;
		this.pointerOver = false;
		this.focusInside = false;
		this.visible = null;
	}

	/** Undo: forget the note on the server, then drop it from the card. Resolves true when forgotten. */
	async forget(id: string): Promise<boolean> {
		if (this.forgetting.has(id)) return false;
		const generation = this.generation;
		this.setForgetting(id, true);
		try {
			await http.delete(Endpoints.KONVO_ASSISTANT_KNOWS_NOTE(id));
		} catch (error) {
			// Already gone (forgotten on another device or in Settings): that is what Undo wanted.
			if (!(error instanceof HttpError && error.status === 404)) {
				logger.warn(`Could not forget note ${id}`, error);
				if (generation === this.generation) this.setForgetting(id, false);
				return false;
			}
		}
		if (generation !== this.generation) return false;
		this.setForgetting(id, false);
		this.dropNote(id);
		this.refreshKnowsPages();
		return true;
	}

	private setForgetting(id: string, on: boolean): void {
		const next = new Set(this.forgetting);
		if (on) next.add(id);
		else next.delete(id);
		this.forgetting = next;
	}

	private dropNote(id: string): void {
		this.pending = removeNote(this.pending, id);
		if (!this.visible) return;
		const notes = removeNote(this.visible.notes, id);
		if (notes.length === 0) {
			this.dismiss();
			return;
		}
		this.visible = {...this.visible, notes};
	}

	/** Change: remember which note the page should open in edit mode, and take the card down. */
	requestEdit(id: string): void {
		this.focusNoteId = id;
		this.takeDown();
	}

	/** See what I know: take the card down (the caller opens the page). */
	openedKnows(): void {
		this.takeDown();
	}

	/**
	 * For Settings › What Konvo knows: the note to open in edit mode, once. Returns null when Change
	 * did not ask for one.
	 */
	takeFocusNote(): string | null {
		const id = this.focusNoteId;
		this.focusNoteId = null;
		return id;
	}

	/**
	 * For Settings › What Konvo knows: call on mount, call the returned function on unmount. While a
	 * page is registered no card goes up for new notes (the waiting ones are dropped, the page lists
	 * them) and `refresh` runs whenever a note is saved or forgotten from the corner.
	 */
	attachKnowsPage(hook: KnowsPageHook = {}): () => void {
		const id = ++knowsPageIds;
		const next = new Map(this.knowsPages);
		next.set(id, hook);
		this.knowsPages = next;
		this.pending = [];
		if (this.visible) this.takeDown();
		return () => this.detachKnowsPage(id);
	}

	private detachKnowsPage(id: number): void {
		if (!this.knowsPages.has(id)) return;
		const next = new Map(this.knowsPages);
		next.delete(id);
		this.knowsPages = next;
	}

	/** The knows store follows along once it has been loaded; open pages get their own refresh too. */
	private refreshKnowsPages(): void {
		if (KonvoKnows.page) KonvoKnows.refresh();
		for (const hook of this.knowsPages.values()) {
			try {
				hook.refresh?.();
			} catch (error) {
				logger.warn('What Konvo knows refresh failed', error);
			}
		}
	}
}

export default new CornerMemoryStore();
